import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';
import { useCurrentUser } from './CurrentUserContext';

const InvoicesContext = createContext(null);

export const useInvoices = () => {
  const context = useContext(InvoicesContext);
  if (!context) throw new Error('useInvoices must be used within an InvoicesProvider');
  return context;
};

// Normalize API response (projectId camelCase) to frontend shape (project_id snake_case)
function normalizeInvoice(inv) {
  return {
    ...inv,
    id: Number(inv.id),
    project_id: inv.projectId ?? inv.project_id ?? null,
    linkedTaskId: inv.linkedTaskId != null ? Number(inv.linkedTaskId) : null,
    lineItems: inv.lineItems || [],
    reminders: inv.reminders || [],
    recurring: inv.recurring || null,
  };
}

export const InvoicesProvider = ({ children }) => {
  const { currentUser, activeWorkspaceId } = useCurrentUser();
  const [invoices, setInvoices] = useState([]);
  // "NOTHING HERE" AND "WE COULD NOT LOAD THIS" ARE DIFFERENT STATES.
  //
  // Carla, 2026-10-03: a failed load used to leave this list empty, and the
  // Invoicing screen then showed "All clear" on the Overdue card — "I would stop
  // chasing money because the app told me there was nothing to chase." A failure
  // that produces a REASSURANCE is worse than silence: silence makes her look.
  //
  // `loadFailed` is that distinction, and it is false only once a load has
  // actually succeeded. Consumers must not render a zero or an all-clear while it
  // is true.
  const [loadFailed, setLoadFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(() => {
    api('/api/invoices')
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data.invoices)) {
          setInvoices(data.invoices.map(normalizeInvoice));
          setLoadFailed(false);
          setLoaded(true);
        } else {
          // A 200 that carries no array is not a success: it leaves the list in
          // whatever state it was in, and must not read as "no invoices".
          setLoadFailed(true);
        }
      })
      .catch(err => {
        console.error('Failed to load invoices:', err);
        setLoadFailed(true);
      });
  }, []);

  // Refetch on workspace switch too — this provider wraps the AppContent remount
  // boundary, so it must key off activeWorkspaceId (mirrors ContactsContext).
  useEffect(() => {
    // Not before the workspace is known (Carla, 2026-10-11): fetched without one, this ran twice on
    // every load, once for no workspace and again when it arrived.
    if (!currentUser?.id || !activeWorkspaceId) return;
    refresh();
  }, [currentUser?.id, activeWorkspaceId, refresh]);

  useEffect(() => {
    window.addEventListener('aidey:invoicesUpdated', refresh);
    return () => window.removeEventListener('aidey:invoicesUpdated', refresh);
  }, [refresh]);

  return (
    <InvoicesContext.Provider value={{ invoices, setInvoices, refresh, loadFailed, loaded }}>
      {children}
    </InvoicesContext.Provider>
  );
};
